import { z } from 'zod';
import { AwardView, ChargeCode, PoolCheck } from './awards.js';
import { ActForOffer, ActingFor } from './acting-for-base.js';
import { Cents, PlainDate } from './common.js';

const Version = z.int().positive();
const Note = z.string().trim().max(2000);
const DecisionDate = z.iso.date().refine((value) => !value.startsWith('0000-'));
const TopUp = z
  .object({
    poolId: z.string().min(1),
    cashCents: z.int().nonnegative(),
    options: z.int().nonnegative().max(2_147_483_647),
  })
  .strict();

/** Authority, current state, proof and budget facts always come from the server. */
export const ApprovalDecision = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('approve'),
      version: Version,
      actingFor: ActingFor.optional(),
      note: Note.optional(),
      boardDecisionDate: DecisionDate.optional(),
      topUps: z.array(TopUp).max(2).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('return'),
      version: Version,
      actingFor: ActingFor.optional(),
      note: Note.min(1),
    })
    .strict(),
  z
    .object({
      action: z.literal('reject'),
      version: Version,
      actingFor: ActingFor.optional(),
      reason: Note.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('decline'),
      version: Version,
      actingFor: ActingFor.optional(),
      reason: Note.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('record_decision'),
      version: Version,
      actingFor: ActingFor.optional(),
      note: Note.optional(),
      boardDecisionDate: DecisionDate.optional(),
      recordedExecutiveId: z.uuid().optional(),
    })
    .strict(),
]);
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;

/** A share of a whole, kept exact ({ num: 1, den: 2 }): a TAA's PAA/TAA-pool share, a PAA's cash. */
export const ShareFraction = z
  .object({ num: z.int().nonnegative(), den: z.int().positive() })
  .strict()
  .refine((value) => value.num <= value.den);
export type ShareFraction = z.infer<typeof ShareFraction>;
const Fraction = ShareFraction;
const ReviewLine = z
  .object({
    lineId: z.uuid(),
    included: z.boolean(),
    cashCents: z.int().nonnegative().optional(),
    options: z.int().nonnegative().max(2_147_483_647).optional(),
    totalValueCents: z.int().nonnegative().optional(),
  })
  .strict();
export const SaveApprovalReview = z
  .object({
    version: Version,
    lines: z.array(ReviewLine).min(1).max(500),
    reason: Note.optional(),
    actingFor: ActingFor.optional(),
    cashShareOverride: Fraction.nullable().optional(),
    taaShareOverride: Fraction.nullable().optional(),
  })
  .strict()
  .refine(
    (value) =>
      (value.cashShareOverride === undefined && value.taaShareOverride === undefined) ||
      !!value.reason?.trim(),
    { message: 'Give a reason for changing the allocation.', path: ['reason'] },
  );
export type SaveApprovalReview = z.infer<typeof SaveApprovalReview>;
export const MoveApprovalPerson = z
  .object({
    version: Version,
    lineId: z.uuid(),
    poolId: z.string().min(1),
    reason: Note.optional(),
    actingFor: ActingFor.optional(),
  })
  .strict();
export type MoveApprovalPerson = z.infer<typeof MoveApprovalPerson>;

export const ApprovalKind = z.enum([
  'delegate',
  'executive',
  'overage',
  'record_decision',
  'self_recognition',
]);
export type ApprovalKind = z.infer<typeof ApprovalKind>;
export const ApprovalActions = z.object({
  approve: z.boolean(),
  return: z.boolean(),
  reject: z.boolean(),
  decline: z.boolean(),
  recordDecision: z.boolean(),
  edit: z.boolean(),
  movePerson: z.boolean(),
  changeCashSplit: z.boolean(),
  changeTaaSplit: z.boolean(),
});
export type ApprovalActions = z.infer<typeof ApprovalActions>;

/**
 * One person's awards in the rolling window of the repeat-recipient rule (SL7 "Last 12 months",
 * QA-006): the same awards Home's team recognition counts (past approval, live lines), as of today.
 */
export const RecentRecognition = z.object({
  awards: z.int().nonnegative(),
  /** Cash plus options at each award's FMV (a PAA's total value), summed. */
  valueCents: Cents,
  /** The latest of those awards: its date and value. */
  last: z.object({ date: PlainDate, valueCents: Cents }).nullable(),
  /** Already a repeat recipient under the rule, before this award. */
  repeat: z.boolean(),
  /** This award, as it stands, would make them one. */
  wouldBeRepeat: z.boolean(),
});
export type RecentRecognition = z.infer<typeof RecentRecognition>;

/** Full review context; the ordinary award view never carries split settings or internal states. */
export const ApprovalDetail = z.object({
  award: AwardView,
  kind: ApprovalKind.nullable(),
  actions: ApprovalActions,
  /** APP-148: for a Stock Admin, who they may act for on this award and what each may do. */
  actingFor: ActForOffer.extend({ actions: ApprovalActions }).nullable().optional(),
  /**
   * APP-148 R125: in the answer to a decision, a review save or a move a Stock Admin made for
   * someone, what was kept as done for them and when the server recorded it, for the
   * confirmation's time (never the browser's clock). Absent on reads and on anything else.
   */
  actedFor: z.object({ id: z.uuid(), at: z.iso.datetime({ offset: true }) }).optional(),
  /** All-conflicted awards stay visible to Executives without decision authority. */
  approvalAvailability: z.enum(['available', 'no_independent_executive']).optional(),
  lines: z.array(
    z.object({
      id: z.uuid(),
      personId: z.uuid(),
      personName: z.string(),
      jobTitle: z.string().nullable(),
      included: z.boolean(),
      cashCents: Cents,
      options: z.int(),
      totalValueCents: Cents.nullable(),
      poolId: z.string(),
      coding: ChargeCode.nullable(),
      /** The amounts as the award was sent, before any change made while it waited. */
      requested: z.object({
        cashCents: Cents,
        options: z.int(),
        totalValueCents: Cents.nullable(),
      }),
      /** Null when this reader may not see the team's recognition (a requester recording one). */
      recent: RecentRecognition.nullable(),
    }),
  ),
  /** When it started waiting: the list is oldest first by this. */
  queuedAt: z.iso.datetime({ offset: true }),
  /** The repeat-recipient rule in force, for the footnote; null when `recent` isn't shown. */
  repeatRule: z
    .object({ awards: z.int().positive(), valueCents: Cents, months: z.int().positive() })
    .nullable(),
  budget: z.array(PoolCheck),
  suggestedTopUps: z.array(TopUp),
  /**
   * A TAA's funding between the PAA/TAA pool and the sector pool, by person (APP-024, APP-036 §2):
   * the PAA/TAA pool's share of the cash the app assigns people to match, the default the award
   * was sent with, and why an Executive changed it. `assignment` is `auto` while the pools are as
   * the app assigned them; any later save or person move makes it `manual`. Null for other types;
   * absent from older servers.
   */
  taaFunding: z
    .object({
      paaShare: ShareFraction,
      defaultShare: ShareFraction,
      reason: z.string().nullable(),
      assignment: z.enum(['auto', 'manual']),
      /**
       * The two pools by name, even one paying no one: a split or a move can put everyone on one
       * pool, and the other must still be offered.
       */
      paaPool: z.object({ id: z.string(), name: z.string() }),
      sectorPool: z.object({ id: z.string(), name: z.string() }),
    })
    .nullable()
    .optional(),
  proofReady: z.boolean(),
  executiveChoices: z.array(z.object({ id: z.uuid(), name: z.string() })),
  /**
   * APP-204: who reads a note sent back with a decline of an over-budget award (whoever entered it,
   * and the Sector Lead it was entered for), and whether each sees the budget (APP-112), so the
   * person declining knows who their words reach. Only for someone who can decline it.
   */
  declineNoteReaders: z.array(z.object({ name: z.string(), seesBudget: z.boolean() })).optional(),
  decision: z.object({
    boardDecisionDate: DecisionDate.nullable(),
    note: z.string().nullable(),
    approvedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    approvedAt: z.string().nullable(),
    recordedExecutive: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  }),
});
export type ApprovalDetail = z.infer<typeof ApprovalDetail>;
export const ApprovalListQuery = z
  .object({ cursor: z.string().min(1).max(512).optional() })
  .strict();
/** Opaque to API consumers; the full database timestamp preserves stable keyset ordering. */
export const ApprovalCursor = z
  .object({
    queuedAt: z.iso.datetime({ precision: 6 }).refine((value) => !value.startsWith('0000-')),
    id: z.uuid(),
  })
  .strict();
export type ApprovalCursor = z.infer<typeof ApprovalCursor>;
/**
 * Whether each of the reader's approval queues has ever had work, so an empty page can tell its
 * first day from "all caught up" (APP-070). False for a role the reader doesn't hold.
 */
export const ApprovalHistory = z.object({
  delegate: z.boolean(),
  executive: z.boolean(),
  requester: z.boolean(),
});
export type ApprovalHistory = z.infer<typeof ApprovalHistory>;

export const ApprovalList = z.object({
  items: z.array(ApprovalDetail),
  nextCursor: z.string().nullable(),
  /** On the first page only. Missing: the page never says "caught up". */
  history: ApprovalHistory.optional(),
});
export type ApprovalList = z.infer<typeof ApprovalList>;
